import React, { useCallback, useEffect, useState } from 'react';
import { ConfigurationFieldStatus, getColorBasedOnStatusAndOverwrite } from '@ydistri/utils';
import { getIconBasedOnStatusAndOverwrite, OverwrittenIcon } from '@ydistri/ds';
import {
  AsoScenarioStoreTierConfigurationResponse,
  AsoScenarioStoreTierType,
} from '@ydistri/api-sdk';
import { styled } from 'styled-components';
import { InputNumber } from 'antd';
import { StoreTierConfigurationField } from './StoreTierConfiguration';

type StoreTierConfigurationInputStatus = 'error' | 'warning';

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

const NumberInput = styled(InputNumber)<{ $color?: React.CSSProperties['color'] }>`
  width: 100%;
  color: ${p => p.$color ?? undefined};
  border-color: ${p => p.$color ?? undefined};

  input,
  .ant-input-number-suffix {
    color: ${p => p.$color ?? undefined};
  }
`;

interface StoreTierConfigurationInputProps {
  disabled: boolean;
  min?: number;
  placeholder?: string;
  precision: number;
  overwritten: boolean;
  status?: StoreTierConfigurationInputStatus;
  feedback?: React.ReactNode;
  value: number | null | undefined;
  onCommit: (value: number | null) => void;
}

export const getStoreTierLabel = (
  storeTier: AsoScenarioStoreTierType | null | undefined,
): string => {
  switch (storeTier) {
    case null:
    case undefined:
    case AsoScenarioStoreTierType.Default:
      return 'Default';
    case AsoScenarioStoreTierType.Tier1:
      return 'Tier 1';
    case AsoScenarioStoreTierType.Tier2:
      return 'Tier 2';
    case AsoScenarioStoreTierType.Tier3:
      return 'Tier 3';
    default:
      return storeTier;
  }
};

export const getConfigurationValue = (
  configuration: AsoScenarioStoreTierConfigurationResponse,
  field: StoreTierConfigurationField,
): number | null | undefined => {
  return configuration[field];
};

export const formatPlaceholderValue = (
  value: number | null | undefined,
  precision: number,
): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  return value.toFixed(precision);
};

export const StoreTierConfigurationInput: React.FC<StoreTierConfigurationInputProps> = ({
  disabled,
  min,
  placeholder,
  precision,
  overwritten,
  status: validationStatus,
  feedback,
  value,
  onCommit,
}) => {
  const propValue = value ?? null;
  const [localValue, setLocalValue] = useState<number | null>(propValue);

  const status = ConfigurationFieldStatus.DEFAULT;
  const color = validationStatus
    ? undefined
    : getColorBasedOnStatusAndOverwrite(status, overwritten, false);
  const suffix = getIconBasedOnStatusAndOverwrite(status, overwritten, OverwrittenIcon.SLIDERS);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- in case of signalR change, we want to change it inside of input
    setLocalValue(value ?? null);
  }, [value]);

  const onChangeHandler = useCallback(
    (nextValue: string | number | null) => {
      setLocalValue(typeof nextValue === 'number' ? nextValue : null);
    },
    [setLocalValue],
  );

  const onBlurHandler = useCallback(() => {
    if (localValue !== propValue) {
      onCommit(localValue);
    }
  }, [localValue, propValue, onCommit]);

  return (
    <FieldStack>
      <NumberInput
        $color={color}
        disabled={disabled}
        precision={precision}
        min={min}
        placeholder={placeholder}
        status={validationStatus}
        suffix={suffix}
        value={localValue}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
      />
      {feedback}
    </FieldStack>
  );
};
